import React, { useState } from "react";

import { NotificationType, Toolbar } from "@transfr-inc/dashboard-components";
import { Button } from "@transfr-inc/dashboard-components/forms";
import { getLearnerTerm } from "@transfr-inc/dashboard-components/constants";

import { BulkArchiveUserModal } from "@components/common/bulk-archive-user-modal";
import { ChangePasswordModal, EditUserModal } from "@components/modals";
import { useNotificationContext } from "@contexts/notification-context";
import { useArchiveUserEnabled } from "@queries";
import container from "@src/container";
import { DeleteTraineeModal } from "@views/users/trainee-detail/modals/delete-trainee";

export function TraineeToolbar({
  trainee,
  onEditTrainee,
  onPasswordUpdated,
  onArchived,
  onUnarchived,
  editEnabled,
}) {
  const [openEditModal, setOpenEditModal] = useState();
  const [openDeleteModal, setOpenDeleteModal] = useState();
  const [openPasswordModal, setOpenPasswordModal] = useState();
  const [openArchiveModal, setOpenArchiveModal] = useState(false);
  const archiveUserEnabled = useArchiveUserEnabled(trainee?.organizationCode);
  const { addNotification } = useNotificationContext();
  const showEditTrainee =
    !trainee?.isAnonymous && !trainee?.isArchived && editEnabled;
  const canArchive =
    archiveUserEnabled && !trainee?.isArchived && !trainee?.isAnonymous;
  const canUnarchive = archiveUserEnabled && trainee?.isArchived;

  const onUnarchive = async () => {
    try {
      await container.userService.unarchiveUsers(
        [trainee.userId],
        trainee.organizationCode
      );
      addNotification({
        type: NotificationType.success,
        icon: ["fa-solid", "circle-check"],
        message: (
          <div>
            <b>
              {trainee.firstName} {trainee.lastName}
            </b>{" "}
            has been unarchived.
          </div>
        ),
      });
      onUnarchived && onUnarchived();
    } catch (error) {
      console.log(error);
      addNotification({
        type: NotificationType.error,
        message: "Something went wrong. Please try again.",
      });
    }
  };

  const onEditModalClose = () => {
    setOpenEditModal(false);
  };

  const onDeleteModalClose = () => {
    setOpenDeleteModal(false);
  };

  const onEditTraineeHandler = (data) => {
    return onEditTrainee(data).then((response) => {
      !response.error && setOpenEditModal();
      return response;
    });
  };

  const onPasswordUpdatedHandler = () => {
    setOpenPasswordModal();
    onPasswordUpdated && onPasswordUpdated();
  };

  const toolbarActions = [
    {
      button: (
        <Button
          size="small"
          key="toolbar-password-trainee-button"
          icon={["fa-regular", "key"]}
          title={"Change Password"}
          tooltip={"Change Password"}
          onClick={() => setOpenPasswordModal(true)}
        ></Button>
      ),
      modal: (
        <ChangePasswordModal
          key="toolbar-password-trainee-modal"
          open={openPasswordModal}
          userIds={[trainee.userId]}
          onCancel={() => setOpenPasswordModal()}
          onPasswordChanged={onPasswordUpdatedHandler}
        ></ChangePasswordModal>
      ),
      enabled: showEditTrainee,
    },
    {
      button: (
        <Button
          size="small"
          key="toolbar-edit-trainee-button"
          icon={["fa-regular", "pen-to-square"]}
          onClick={() => setOpenEditModal(true)}
          title={`Edit ${getLearnerTerm()}`}
          tooltip={`Edit ${getLearnerTerm()}`}
        ></Button>
      ),
      modal: (
        <EditUserModal
          key="toolbar-edit-trainee-modal"
          open={openEditModal}
          user={trainee}
          onClose={onEditModalClose}
          onUpdateUser={onEditTraineeHandler}
        ></EditUserModal>
      ),
      enabled: showEditTrainee,
    },
    {
      button: (
        <Button
          size="small"
          key="toolbar-archive-trainee-button"
          icon={["fa-light", "box-archive"]}
          onClick={() => setOpenArchiveModal(true)}
          title={`Archive ${getLearnerTerm()}`}
          tooltip={`Archive ${getLearnerTerm()}`}
        ></Button>
      ),
      modal: (
        <BulkArchiveUserModal
          key="toolbar-archive-trainee-modal"
          open={openArchiveModal}
          learners={[trainee]}
          orgCode={trainee?.organizationCode}
          onCancel={() => setOpenArchiveModal(false)}
          onArchived={() => onArchived && onArchived()}
        />
      ),
      enabled: canArchive,
    },
    {
      button: (
        <Button
          size="small"
          key="toolbar-unarchive-trainee-button"
          icon={["fa-light", "box-open"]}
          onClick={onUnarchive}
          title={`Unarchive ${getLearnerTerm()}`}
          tooltip={`Unarchive ${getLearnerTerm()}`}
        ></Button>
      ),
      modal: null,
      enabled: canUnarchive,
    },
    {
      button: (
        <Button
          size="small"
          key="toolbar-delete-trainee-button"
          icon={["fa-regular", "trash-can"]}
          onClick={() => setOpenDeleteModal(true)}
          title={`Delete ${getLearnerTerm()}`}
          tooltip={`Delete ${getLearnerTerm()}`}
        ></Button>
      ),
      modal: (
        <DeleteTraineeModal
          key="toolbar-delete-trainee-modal"
          trainee={trainee}
          open={openDeleteModal}
          onClose={onDeleteModalClose}
        ></DeleteTraineeModal>
      ),
      enabled: false,
    },
  ];

  return (
    <>
      <Toolbar>
        {toolbarActions.map(({ enabled, button }) => enabled && button)}
      </Toolbar>
      {toolbarActions.map(({ enabled, modal }) => enabled && modal)}
    </>
  );
}
